// SL.L2 (#2958), W5 — the one shared "Plan next sprint" button, reused on the
// Board header (L1) and the Phase 4 card (L3). It renders when the server
// lists `plan_next` (ruling #2950 6229870839), which it does whether or not a
// draft sprint exists; the server hides it only while an approved sprint
// waits to start. The page passes the allowedActions of GET /sprints, the
// payload that carries plan_next (open point B), and re-reads it in onPlanned.
//
// Click: C1's create-or-resume (POST …/sprints/plan-next). `created` tells
// a new draft (201) from a waiting one (200). A refusal (409: Fast Track, or
// an approved sprint waiting) shows the server's text; nothing is swallowed
// (#3055). No re-read after a refusal, so its message stays on screen.
// After a success the re-read may drop plan_next (R1 P3): the notice stays,
// without the button.
import { useState } from 'react';
import { hasAction } from '../lib/sprintLoopActions';
import { planNextSprint } from '../lib/backlogApi';

export interface PlanNextSprintButtonProps {
  projectId: string;
  allowedActions: readonly string[] | undefined;
  /** After a draft is created or returned: re-read what the page shows. */
  onPlanned?: () => void | Promise<void>;
}

export default function PlanNextSprintButton({ projectId, allowedActions, onPlanned }: PlanNextSprintButtonProps) {
  const [notice, setNotice] = useState<{ text: string; error: boolean } | null>(null);
  const [planning, setPlanning] = useState(false);
  const canPlan = hasAction(allowedActions, 'plan_next');
  if (!canPlan && !notice) return null;

  async function handleClick() {
    if (planning) return; // the disabled button already stops a second click (R1 P3)
    setPlanning(true);
    setNotice(null);
    try {
      const { sprint, created, adoptedPlan } = await planNextSprint(projectId);
      // SL.D1 (#2951, 6406880252 item 7): say which plan a planless draft took.
      const adopted = adoptedPlan ? ` — planned from "${adoptedPlan.title}" (v${adoptedPlan.version})` : '';
      setNotice({ text: `${created ? `Sprint ${sprint.number} draft created` : `Sprint ${sprint.number} draft is ready`}${adopted}`, error: false });
      // TODO(SL.E1): open the Sprint Planning session bound to sprint.id. Today
      // session create takes no sprintId and resumes per (project, agent), so
      // /chat?agentType=sprint_planning would open a session not bound to this
      // draft (#2958 6252826074). E1 (#2955) adds the binding.
      await onPlanned?.();
    } catch (e) {
      setNotice({ text: e instanceof Error ? e.message : String(e), error: true });
    } finally {
      setPlanning(false);
    }
  }

  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 'var(--space-2)', flexWrap: 'wrap' }}>
      {canPlan && <button
        type="button"
        onClick={() => void handleClick()}
        disabled={planning}
        style={{
          padding: '8px 16px', borderRadius: 'var(--radius-md)', border: 'none',
          background: 'var(--primary)', color: '#fff', fontFamily: 'var(--font-body)',
          fontSize: 13, fontWeight: 600, cursor: planning ? 'wait' : 'pointer', opacity: planning ? 0.7 : 1,
        }}
      >
        Plan next sprint
      </button>}
      {notice && (
        <span
          role={notice.error ? 'alert' : 'status'}
          style={{ fontFamily: 'var(--font-body)', fontSize: 12, color: notice.error ? 'var(--error)' : 'var(--text-secondary)' }}
        >
          {notice.text}
        </span>
      )}
    </span>
  );
}
